import React, { useState, useEffect } from 'react';
import { Bell, Check, Trash2, ShoppingBag, User, Package } from 'lucide-react';
import { collection, getDocs, updateDoc, deleteDoc, doc, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';

const NotificationsPanel = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(
        query(collection(db, COLLECTIONS.NOTIFICATIONS), orderBy('createdAt', 'desc'))
      );
      const notifs = [];
      querySnapshot.forEach((doc) => {
        notifs.push({ id: doc.id, ...doc.data() });
      });
      setNotifications(notifs);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notifId) => {
    try {
      await updateDoc(doc(db, COLLECTIONS.NOTIFICATIONS, notifId), { read: true });
      loadNotifications();
    } catch (error) {
      console.error('Error marking as read:', error);
      if (error.code === 'not-found') {
        setNotifications(prev => prev.filter(n => n.id !== notifId));
      }
    }
  };

  const deleteNotification = async (notifId) => {
    try {
      await deleteDoc(doc(db, COLLECTIONS.NOTIFICATIONS, notifId));
      setNotifications(prev => prev.filter(n => n.id !== notifId));
    } catch (error) {
      console.error('Error deleting notification:', error);
      if (error.code === 'not-found') {
        setNotifications(prev => prev.filter(n => n.id !== notifId));
      }
    }
  };

  const filteredNotifications = notifications.filter(notif => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !notif.read;
    if (filter === 'read') return notif.read;
    return notif.type === filter;
  });

  const getIcon = (type) => {
    switch (type) {
      case 'order': return <ShoppingBag className="w-5 h-5" />;
      case 'user': return <User className="w-5 h-5" />;
      case 'product': return <Package className="w-5 h-5" />;
      default: return <Bell className="w-5 h-5" />;
    }
  };

  const getColor = (type) => {
    switch (type) {
      case 'order': return 'bg-blue-100 text-blue-700';
      case 'user': return 'bg-green-100 text-green-700';
      case 'product': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">الإشعارات</h1>
          <p className="text-gray-600 mt-2">
            غير مقروءة: {notifications.filter(n => !n.read).length}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex flex-wrap gap-2">
          {[
            { value: 'all', label: 'الكل' },
            { value: 'unread', label: 'غير مقروءة' },
            { value: 'read', label: 'مقروءة' },
            { value: 'order', label: 'طلبات' },
            { value: 'user', label: 'مستخدمين' },
            { value: 'product', label: 'منتجات' }
          ].map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${filter === f.value
                ? 'bg-yellow-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {filteredNotifications.length > 0 ? (
        <div className="space-y-3">
          {filteredNotifications.map((notif) => (
            <div
              key={notif.id}
              className={`bg-white rounded-xl shadow-md p-6 transition-all ${!notif.read ? 'border-r-4 border-yellow-500' : ''
                }`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg ${getColor(notif.type)}`}>
                  {getIcon(notif.type)}
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-bold text-gray-900">{notif.title}</h3>
                      <p className="text-sm text-gray-600 mt-1">{notif.message}</p>
                      <p className="text-xs text-gray-400 mt-2">
                        {notif.createdAt?.toDate?.()?.toLocaleString('ar-EG') || 'الآن'}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {!notif.read && (
                        <button
                          onClick={() => markAsRead(notif.id)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="تعليم كمقروء"
                        >
                          <Check className="w-5 h-5" />
                        </button>
                      )}
                      <button
                        onClick={() => deleteNotification(notif.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="حذف"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md p-12 text-center text-gray-500">
          <Bell className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p>لا توجد إشعارات</p>
        </div>
      )}
    </div>
  );
};

export default NotificationsPanel;
